import { useCallback, useEffect, useState } from "react";

import type { AtlasPayload, PlanetIdea } from "./types";

type State =
  | { status: "loading"; data: null; error: null }
  | { status: "ready"; data: AtlasPayload; error: null }
  | { status: "error"; data: null; error: string };

/**
 * Fetch the sky.  One GET, no polling — the atlas is a map, and a map
 * that refreshed itself under a reader's finger would be worse than a
 * stale one.  Sessions push new evidence, so the page reloads when a
 * learner comes back to it.
 */
export function useAtlas() {
  const [state, setState] = useState<State>({ status: "loading", data: null, error: null });

  const load = useCallback(() => {
    let cancelled = false;
    setState({ status: "loading", data: null, error: null });
    fetch("/api/coaching/atlas", { credentials: "include" })
      .then(async (response) => {
        if (!response.ok) throw new Error(`atlas request failed (${response.status})`);
        return (await response.json()) as AtlasPayload;
      })
      .then((data) => {
        if (!cancelled) setState({ status: "ready", data, error: null });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            status: "error",
            data: null,
            error: error instanceof Error ? error.message : "could not reach the atlas",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  return { ...state, reload: load };
}

/**
 * One world's card, fetched when its disclosure opens and remembered for
 * the rest of the visit.  The server answers a re-request with a 304 and
 * the browser cache serves the body, but a promise per world is cheaper
 * still: re-opening a card costs nothing at all.
 */
const IDEAS = new Map<string, Promise<PlanetIdea>>();

function fetchIdea(planetId: string): Promise<PlanetIdea> {
  const cached = IDEAS.get(planetId);
  if (cached) return cached;
  const request = fetch(`/api/coaching/atlas/planets/${encodeURIComponent(planetId)}/idea`, {
    credentials: "include",
  })
    .then(async (response) => {
      if (!response.ok) throw new Error(`the card did not load (${response.status})`);
      return (await response.json()) as PlanetIdea;
    })
    .catch((error: unknown) => {
      // A failed fetch is not a fact about the world; forget it so the
      // next open tries again.
      IDEAS.delete(planetId);
      throw error;
    });
  IDEAS.set(planetId, request);
  return request;
}

export type IdeaState =
  | { status: "idle"; idea: null; error: null }
  | { status: "loading"; idea: null; error: null }
  | { status: "ready"; idea: PlanetIdea; error: null }
  | { status: "error"; idea: null; error: string };

/** The card for `planetId`, or idle while `planetId` is null (folded). */
export function usePlanetIdea(planetId: string | null): IdeaState {
  const [state, setState] = useState<IdeaState>({ status: "idle", idea: null, error: null });
  useEffect(() => {
    if (!planetId) {
      setState({ status: "idle", idea: null, error: null });
      return undefined;
    }
    let cancelled = false;
    setState({ status: "loading", idea: null, error: null });
    fetchIdea(planetId)
      .then((idea) => {
        if (!cancelled) setState({ status: "ready", idea, error: null });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            status: "error",
            idea: null,
            error: error instanceof Error ? error.message : "the card did not load",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [planetId]);
  return state;
}

/** True while the viewport is phone-shaped.  Drives layout, not content. */
export function useNarrow(breakpoint = 640) {
  const [narrow, setNarrow] = useState(() =>
    typeof window === "undefined" ? false : window.innerWidth <= breakpoint,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const query = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = () => setNarrow(query.matches);
    onChange();
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, [breakpoint]);
  return narrow;
}

/** The viewer's colour scheme, remembered for this device. */
export function useNightSky() {
  const [night, setNight] = useState(() => {
    try {
      const stored = window.localStorage.getItem("sharpana.atlas.sky");
      if (stored === "night") return true;
      if (stored === "day") return false;
      return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? true;
    } catch {
      return true;
    }
  });
  const toggle = useCallback(() => {
    setNight((was) => {
      try {
        window.localStorage.setItem("sharpana.atlas.sky", was ? "day" : "night");
      } catch {
        /* a viewer who blocks storage still gets to toggle */
      }
      return !was;
    });
  }, []);
  return { night, toggle };
}
